// ============================================================
// Shared building blocks for the v2 report pages.
// ============================================================

import type { ReactNode } from 'react'
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import { Insights, Verified } from '@mui/icons-material'
import ReactECharts from 'echarts-for-react'

import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { setSubTenant } from '@/app/projectSlice'
import { DateRangePicker } from '@/components/shared/DateRangePicker'
import { EmptyState } from '@/components/shared/EmptyState'
import { bucketLabel, v2ErrorMessage, type BreakdownRow } from '@/apis/v2Mappers'
import type { Period } from '@/utils/dates'

// ---------- formatting ----------

export function fmtNum(v: number | null | undefined, digits = 0): string {
    if (v === null || v === undefined || !Number.isFinite(v)) {
        return '—'
    }
    return v.toLocaleString(undefined, { maximumFractionDigits: digits })
}

/** 0.1234 → "12.3%" */
export function fmtPct(v: number | null | undefined): string {
    if (v === null || v === undefined || !Number.isFinite(v)) {
        return '—'
    }
    return `${(v * 100).toFixed(1)}%`
}

export function fmtMs(v: number | null | undefined): string {
    if (v === null || v === undefined || !Number.isFinite(v)) {
        return '—'
    }
    if (v < 1000) {
        return `${Math.round(v)} ms`
    }
    const s = v / 1000
    if (s < 60) {
        return `${s.toFixed(1)} s`
    }
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

export function fmtMoney(v: number | null | undefined, currency = 'USD'): string {
    if (v === null || v === undefined || !Number.isFinite(v)) {
        return '—'
    }
    try {
        return v.toLocaleString(undefined, { style: 'currency', currency })
    } catch {
        return `${v.toFixed(2)} ${currency}`
    }
}

/** ISO string, epoch ms, or SQLite `YYYY-MM-DD HH:MM:SS` (UTC) → local date/time. */
export function fmtDateTime(value: string | number): string {
    const raw =
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
            ? `${value.replace(' ', 'T')}Z`
            : value
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
}

// ---------- page chrome ----------

interface ReportHeaderProps {
    title: string
    subtitle?: string
    period?: Period
    onPeriodChange?: (p: Period) => void
    actions?: ReactNode
}

export function ReportHeader({
    title,
    subtitle,
    period,
    onPeriodChange,
    actions,
}: ReportHeaderProps) {
    const subTenant = useAppSelector(s => s.project.subTenant)
    const dispatch = useAppDispatch()
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
            }}
        >
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5">{title}</Typography>
                    {subTenant !== null && (
                        <Chip
                            size="small"
                            color="secondary"
                            label={`Sub-tenant: ${subTenant === '' ? '(project itself)' : subTenant}`}
                            onDelete={() => dispatch(setSubTenant(null))}
                        />
                    )}
                </Box>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {actions}
                {period && onPeriodChange && (
                    <DateRangePicker value={period} onChange={onPeriodChange} />
                )}
            </Box>
        </Box>
    )
}

interface ReportStatusProps {
    isLoading?: boolean
    error?: unknown
    isEmpty?: boolean
    emptyIcon?: ReactNode
    emptyTitle?: string
    emptyDescription?: string
    children?: ReactNode
}

/** Loading / API error / empty handling shared by every report. */
export function ReportStatus({
    isLoading,
    error,
    isEmpty,
    emptyIcon,
    emptyTitle = 'No data for this range',
    emptyDescription,
    children,
}: ReportStatusProps) {
    if (isLoading) {
        return (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Box>
        )
    }
    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {v2ErrorMessage(error)}
            </Alert>
        )
    }
    if (isEmpty) {
        return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
    }
    return <>{children}</>
}

// ---------- KPI tile ----------

interface KpiProps {
    title: string
    value: string
    change?: number | null
    /** Lower is better (bounce rate, errors). */
    invert?: boolean
    exact?: boolean
    /** Sampling-based estimate (Analytics Engine / HyperLogLog): shows the Estimate chip. */
    estimate?: boolean
    hint?: string
}

export function Kpi({ title, value, change, invert, exact, estimate, hint }: KpiProps) {
    const good = change === null || change === undefined ? null : invert ? change <= 0 : change >= 0
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {title}
                    </Typography>
                    {exact && <ExactChip />}
                    {!exact && estimate && <EstimateChip />}
                </Box>
                <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
                    <Typography variant="h4">{value}</Typography>
                </Tooltip>
                {change !== null && change !== undefined && (
                    <Typography
                        variant="body2"
                        sx={{ color: good ? 'success.main' : 'error.main', mt: 0.5 }}
                    >
                        {change >= 0 ? '+' : ''}
                        {change.toFixed(1)}% vs previous period
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

export function ExactChip() {
    return (
        <Tooltip title="Exact count: conversion events are stored unsampled, so this number is not an estimate.">
            <Chip
                icon={<Verified sx={{ fontSize: 14 }} />}
                label="Exact"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
            />
        </Tooltip>
    )
}

const ESTIMATE_HELP =
    'Estimate: computed from sampled events (sampling-weighted counts, approximate distinct visitors), so it can differ slightly from the exact total. Conversion events marked Exact are never sampled.'

/** The report (or number) is a sampling-based estimate, not an exact count. */
export function EstimateChip({ title = ESTIMATE_HELP }: { title?: string }) {
    return (
        <Tooltip title={title}>
            <Chip
                icon={<Insights sx={{ fontSize: 14 }} />}
                label="Estimate"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
            />
        </Tooltip>
    )
}

// ---------- tables & charts ----------

interface BreakdownTableProps {
    rows: BreakdownRow[]
    keyLabel: string
    countLabel?: string
    showValue?: boolean
    formatKey?: (key: string) => ReactNode
    maxRows?: number
}

export function BreakdownTable({
    rows,
    keyLabel,
    countLabel = 'Events',
    showValue,
    formatKey,
    maxRows,
}: BreakdownTableProps) {
    const list = maxRows ? rows.slice(0, maxRows) : rows
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>{keyLabel}</TableCell>
                    <TableCell align="right">{countLabel}</TableCell>
                    <TableCell align="right">Sessions</TableCell>
                    <TableCell align="right">Visitors</TableCell>
                    {showValue && <TableCell align="right">Value</TableCell>}
                    <TableCell sx={{ width: 140 }}>Share</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {list.map(r => (
                    <TableRow key={r.key} hover>
                        <TableCell sx={{ maxWidth: 360 }}>
                            <Typography variant="body2" noWrap title={r.key}>
                                {formatKey ? formatKey(r.key) : r.key}
                            </Typography>
                        </TableCell>
                        <TableCell align="right">{fmtNum(r.count)}</TableCell>
                        <TableCell align="right">{fmtNum(r.sessions)}</TableCell>
                        <TableCell align="right">{fmtNum(r.visitors)}</TableCell>
                        {showValue && <TableCell align="right">{fmtNum(r.value, 2)}</TableCell>}
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, (r.share ?? 0) * 100)}
                                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" sx={{ minWidth: 40 }}>
                                    {fmtPct(r.share)}
                                </Typography>
                            </Box>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

export const SERIES_COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#0ea5e9']

interface LineChartCardProps {
    title: string
    rows: Array<Record<string, number | string>>
    series: Array<{ key: string; label: string }>
    interval: string
    height?: number
    /** Series colors (defaults to SERIES_COLORS) — e.g. a portal's brand colors. */
    colors?: readonly string[]
}

export function LineChartCard({
    title,
    rows,
    series,
    interval,
    height = 300,
    colors = SERIES_COLORS,
}: LineChartCardProps) {
    const option = {
        tooltip: { trigger: 'axis' as const },
        legend: { data: series.map(s => s.label) },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
            type: 'category' as const,
            data: rows.map(r => bucketLabel(String(r.t), interval)),
        },
        yAxis: { type: 'value' as const },
        series: series.map((s, i) => ({
            name: s.label,
            type: 'line' as const,
            smooth: true,
            data: rows.map(r => Number(r[s.key] ?? 0)),
            areaStyle: { opacity: 0.08 },
            itemStyle: { color: colors[i % colors.length] },
        })),
    }
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {title}
                </Typography>
                {rows.length > 0 ? (
                    <ReactECharts option={option} style={{ height }} notMerge />
                ) : (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No data for this range
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

export function SectionCard({
    title,
    action,
    children,
}: {
    title: string
    action?: ReactNode
    children: ReactNode
}) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        gap: 1,
                    }}
                >
                    <Typography variant="h6">{title}</Typography>
                    {action}
                </Box>
                {children}
            </CardContent>
        </Card>
    )
}
